import { Menu, Search } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../components/Button";
import { StatusBadge } from "../components/Badge";
import { commandPaletteHint } from "../lib/keyboard";
import type { QuoteStatus } from "../api/types";

// 頂列:目前這一頁是什麼(報價單號 / 標題 / 狀態 / 未存提示),
// 加上兩個由外框注入的入口 —— 窄螢幕的選單鈕與搜尋鈕。
//
// 頂列**不放動作按鈕**。儲存 / 匯出 / 寄送都是「對某一份文件」動手,
// 它們的啟用條件(dirty、稅率合法與否)只有編輯器自己知道,
// 所以那組工具列做在 QuoteEditor 的表頭上。
// 早期這裡也長了一份儲存 / 匯出 / 寄送的按鈕,由四個回呼 prop 控制,
// 但從來沒有人傳過那四個 prop —— 是純死碼,已經清掉,不要再長回來。
// tests/app-shell.test.tsx 的「頂列」那一組會盯著。

export interface TopBarProps {
  /** 目前畫面的標題,例如專案名稱或「報價單」。 */
  title?: string;
  /** 報價單號,例 NB-2026-0001。 */
  quoteNo?: string;
  status?: QuoteStatus;
  /** 有未存變更時在標題旁標示。 */
  dirty?: boolean;
  /** 開啟全域搜尋。由外框注入。 */
  onOpenSearch?: () => void;
  /** <1024px 時開啟側邊欄抽屜。由外框注入。 */
  onOpenMenu?: () => void;
  /**
   * 通知鈴鐺。由外框注入(見 `features/notifications/NotificationBell`)。
   *
   * 這裡收的是節點而不是「未讀數 + 回呼」兩個 prop:鈴鐺自己要處理徽章、
   * 可存取名稱與 aria-expanded,那一套規矩屬於通知這個功能,不屬於頂列。
   * 頂列只負責決定它擺在哪。
   */
  bell?: ReactNode;
}

export function TopBar({ title, quoteNo, status, dirty = false, onOpenSearch, onOpenMenu, bell }: TopBarProps) {
  return (
    <header className="sticky top-0 z-30 flex flex-wrap items-center gap-2 border-b border-[var(--border)] bg-[var(--surface)] px-3 py-2">
      {onOpenMenu ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenMenu}
          aria-label="開啟選單"
          icon={<Menu aria-hidden="true" size={18} />}
          className="md:hidden"
        />
      ) : null}

      <div className="flex min-w-0 flex-1 items-center gap-2">
        {quoteNo ? <span className="tabular shrink-0 text-sm text-[var(--text-3)]">{quoteNo}</span> : null}
        {title ? (
          <h1 className="m-0 min-w-0 truncate text-base font-semibold text-[var(--text)]">{title}</h1>
        ) : null}
        {status ? <StatusBadge status={status} /> : null}
        {dirty ? <span className="shrink-0 text-xs text-[var(--warn)]">尚未儲存</span> : null}
      </div>

      {bell}

      {onOpenSearch ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenSearch}
          icon={<Search aria-hidden="true" size={16} />}
          aria-label={`搜尋(${commandPaletteHint()})`}
          className="md:hidden"
        />
      ) : null}
    </header>
  );
}
